/**
 * The page's favicons, from the brand — media's thumbnails of the mark the tenant uploaded.
 *
 * Every public page (`links`, `store`, `checkout`, `subscriptions`) used to point its `<link
 * rel="icon">` at a per-app folder on the `icons` CDN, drawn by hand once and never touched when
 * the brand's mark changed. Since 2026-10-04 the brand answers `favicon_url` (media's 32px
 * thumbnail of `logo_url`) and `touch_icon_url` (256px) — or the tenant's `icons.<apex>` set when the
 * mark is not a media upload — and this writes them into the document head, so a new mark reaches
 * every tab icon on the next page load with no deploy anywhere.
 *
 * It REPLACES the hrefs of the links a page already declares (its `index.html` keeps a static set so
 * the very first paint, and a crawler, has one) and adds one only when none exists.
 */

/** What setting the icons needs of a brand. Both optional: a cache may predate the fields. */
export interface BrandIcons {
  favicon_url?: string | null;
  touch_icon_url?: string | null;
}

function setIcon(rel: string, href: string, sizes?: string): void {
  const existing = [...document.head.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`)];
  if (existing.length === 0) {
    const link = document.createElement('link');
    link.rel = rel;
    if (sizes) link.setAttribute('sizes', sizes);
    link.href = href;
    document.head.appendChild(link);
    return;
  }
  for (const link of existing) {
    if (link.href !== href) link.href = href;
    // A media thumbnail is a PNG or a JPEG under an extensionless key, never an `.ico`: a stale
    // `type="image/x-icon"` would make some browsers skip it.
    link.removeAttribute('type');
  }
}

/** Point the document's favicon and touch icon at the brand's. A no-op without a brand or a DOM. */
export function applyBrandIcons(brand: BrandIcons | null | undefined): void {
  if (typeof document === 'undefined' || !brand) return;
  const favicon = (brand.favicon_url ?? '').trim();
  const touch = (brand.touch_icon_url ?? '').trim();
  if (favicon.startsWith('https://')) setIcon('icon', favicon, '32x32');
  if (touch.startsWith('https://')) setIcon('apple-touch-icon', touch, '180x180');
}
